/**
 * Template list (flow-creation dialog, tab "from template")
 *
 * Everything comes from the store: the public entries in the user's language plus the
 * tenant's own saved flows. The old three-question filter (internal/external, connection
 * approach, style) is gone; the flow builder in step 3 answers those questions, and the list
 * shows what each entry is for as chips instead.
 */

import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { Chip, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CampaignTemplate } from '../../campaign-builder.types';

interface ItemProps {
  template: CampaignTemplate;
  loading: boolean;

  handleApplyTemplate(): void;
}

const TemplateItem: React.FC<ItemProps> = ({ template, loading, handleApplyTemplate }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const chips: string[] = [];
  if (template.visibility === 'tenant') chips.push(t('storeTemplateOwn'));
  if (template.language === 'de') chips.push(t('flowBuilderLangDe'));
  if (template.language === 'en') chips.push(t('flowBuilderLangEn'));
  if (template.formalSpeech === true) chips.push(t('politeSpeech'));
  if (template.formalSpeech === false) chips.push(t('casualSpeech'));
  if (template.contactAudience) chips.push(t(`flowBuilderAudience_${template.contactAudience}`));
  if (template.stepCount) chips.push(t('storeTemplateSteps', { count: template.stepCount }));

  return (
    <Stack className={classes.templateItem} gap={3} direction={'row'} alignItems={'flex-start'}>
      <LibraryBooksIcon className={classes.icon} />
      <Stack gap={2} sx={{ flex: 1 }}>
        <div>
          <Typography className={classes.templateTitle}>{template.name}</Typography>
          {template.description && <Typography className={classes.templateDescription}>{template.description}</Typography>}
          {template.audience && (
            <Typography className={classes.templateAudience}>
              <strong>{t('forWhom')}: </strong>
              {template.audience}
            </Typography>
          )}
          {chips.length > 0 && (
            <Stack direction={'row'} flexWrap={'wrap'} gap={0.75} className={classes.chips}>
              {chips.map((chip) => (
                <Chip key={chip} size={'small'} label={chip} variant={'outlined'} />
              ))}
            </Stack>
          )}
        </div>
        <Stack direction={'row'} justifyContent={'flex-end'}>
          <PaiButton text={t('selectTemplate')} border={'rounded'} size={'small'} loading={loading} action={handleApplyTemplate} />
        </Stack>
      </Stack>
    </Stack>
  );
};

interface Props {
  templates: CampaignTemplate[];
  loading: boolean;

  handleApplyTemplate(templateId: string): () => void;
}

const TemplateList: React.FC<Props> = ({ templates, loading, handleApplyTemplate }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  if (templates.length === 0) {
    return <Typography className={classes.empty}>{t('storeTemplatesEmpty')}</Typography>;
  }

  return (
    <Stack gap={3}>
      <Typography className={classes.hint}>{t('storeTemplatesHint')}</Typography>
      {templates.map((template) => (
        <TemplateItem key={template._id} template={template} loading={loading} handleApplyTemplate={handleApplyTemplate(template._id)} />
      ))}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  templateItem: {
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    paddingBottom: '24px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
  },
  templateTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  templateDescription: {
    fontSize: theme.typography.pxToRem(15),
    marginBottom: '8px',
  },
  templateAudience: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
  },
  chips: {
    marginTop: '8px',
  },
  hint: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
  },
  empty: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
}));

export default TemplateList;
